import { ReactNode } from "react";

import { MenuAccountHeader, MenuGroup, MenuHeader, MenuItem } from "./types";

export interface MenuView {
  groups: MenuGroup[];
  parent?: MenuItem;
  content?: ReactNode;
}

export function hasSubmenu(item: MenuItem): boolean {
  return !!item.submenu && item.submenu.length > 0;
}

export function isDrillable(item: MenuItem): boolean {
  return hasSubmenu(item) || item.submenuContent != null;
}

/**
 * Walks the drill-down stack of item keys from the root groups and returns
 * the view the panel should render: a set of item groups, or a terminal
 * content panel (`content`) for an item with `submenuContent`. A key that no
 * longer resolves — the caller re-rendered with different groups — stops the
 * walk at the deepest level that still exists.
 */
export function resolveView(groups: MenuGroup[], stack: string[]): MenuView {
  let view: MenuView = { groups };
  for (const key of stack) {
    const item = view.groups
      .flatMap((group) => group.items)
      .find((candidate) => candidate.key === key);
    if (!item) {
      return view;
    }
    if (item.submenuContent != null) {
      return { groups: [], parent: item, content: item.submenuContent };
    }
    if (!hasSubmenu(item)) {
      return view;
    }
    view = { groups: item.submenu as MenuGroup[], parent: item };
  }
  return view;
}

export function isAccountHeader(
  header: MenuHeader,
): header is MenuAccountHeader {
  return "name" in header;
}

export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}
